import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Grid, Paper, Chip,
  IconButton, Alert, CircularProgress, Divider,
  useMediaQuery, alpha, Avatar, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Build as BuildIcon,
  Memory as MemoryIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

// Componente para adicionar computador
function ComputadorForm({ onAdd, onCancel, clientes, computadoresCliente }) {
  const [formData, setFormData] = useState({
    computador_id: '',
    servicos: [],
    pecas: [],
    diagnosticos: [],
    relato_cliente: '',
    mao_obra: 0
  });

  const [novaPeca, setNovaPeca] = useState({
    descricao: '',
    quantidade: 1,
    valor_unitario: 0
  });

  const diagnosticosComuns = [
    'Superaquecimento', 'Lento', 'Não liga', 'Tela azul',
    'Barulho estranho', 'Não carrega', 'Teclas não funcionam'
  ];

  const servicosComuns = [
    'Limpeza Física', 'Troca Pasta Térmica', 'Formatação',
    'Instalação de SO', 'Backup', 'Otimização de Sistema',
    'Troca de HD/SSD', 'Upgrade de Memória', 'Troca de Fonte'
  ];

  const handleAddPeca = () => {
    if (!novaPeca.descricao) {
      toast.warning('Digite a descrição da peça');
      return;
    }
    const total = (novaPeca.quantidade || 1) * (novaPeca.valor_unitario || 0);
    setFormData(prev => ({
      ...prev,
      pecas: [...prev.pecas, { ...novaPeca, valor_total: total }],
      valor_pecas: (prev.valor_pecas || 0) + total
    }));
    setNovaPeca({ descricao: '', quantidade: 1, valor_unitario: 0 });
  };

  const handleRemovePeca = (index) => {
    const peca = formData.pecas[index];
    setFormData(prev => ({
      ...prev,
      pecas: prev.pecas.filter((_, i) => i !== index),
      valor_pecas: (prev.valor_pecas || 0) - (peca.valor_total || 0)
    }));
  };

  const handleAddServico = (servico) => {
    if (formData.servicos.includes(servico)) {
      setFormData(prev => ({
        ...prev,
        servicos: prev.servicos.filter(s => s !== servico)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        servicos: [...prev.servicos, servico]
      }));
    }
  };

  const handleAddDiagnostico = (diagnostico) => {
    if (formData.diagnosticos.includes(diagnostico)) {
      setFormData(prev => ({
        ...prev,
        diagnosticos: prev.diagnosticos.filter(d => d !== diagnostico)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        diagnosticos: [...prev.diagnosticos, diagnostico]
      }));
    }
  };

  const handleSubmit = () => {
    if (!formData.computador_id) {
      toast.warning('Selecione um computador');
      return;
    }
    const computador = computadoresCliente.find(c => c.id === formData.computador_id);
    onAdd({
      ...formData,
      computador_nome: computador?.nome_identificacao || 'Computador'
    });
  };

  return (
    <Card sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
          <ComputerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Adicionar Computador
        </Typography>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Selecione o Computador</InputLabel>
            <Select
              value={formData.computador_id}
              label="Selecione o Computador"
              onChange={(e) => setFormData({ ...formData, computador_id: e.target.value })}
            >
              {computadoresCliente.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome_identificacao} - {c.marca} {c.modelo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Relato do Cliente (para este computador)"
            multiline
            rows={2}
            value={formData.relato_cliente}
            onChange={(e) => setFormData({ ...formData, relato_cliente: e.target.value })}
          />
        </Grid>

        {/* Diagnósticos */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
            Diagnósticos
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {diagnosticosComuns.map(d => (
              <Chip
                key={d}
                label={d}
                onClick={() => handleAddDiagnostico(d)}
                color={formData.diagnosticos.includes(d) ? 'primary' : 'default'}
                size="small"
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        </Grid>

        {/* Serviços */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
            Serviços
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {servicosComuns.map(s => (
              <Chip
                key={s}
                label={s}
                onClick={() => handleAddServico(s)}
                color={formData.servicos.includes(s) ? 'primary' : 'default'}
                size="small"
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        </Grid>

        {/* Peças */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
            Peças
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
            {formData.pecas.map((p, i) => (
              <Chip
                key={i}
                label={`${p.descricao} (${p.quantidade}x R$ ${(p.valor_unitario || 0).toFixed(2)})`}
                onDelete={() => handleRemovePeca(i)}
                size="small"
                color="secondary"
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Descrição"
                value={novaPeca.descricao}
                onChange={(e) => setNovaPeca({ ...novaPeca, descricao: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Qtd"
                value={novaPeca.quantidade}
                onChange={(e) => setNovaPeca({ ...novaPeca, quantidade: parseFloat(e.target.value) || 1 })}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Valor Unit."
                value={novaPeca.valor_unitario}
                onChange={(e) => setNovaPeca({ ...novaPeca, valor_unitario: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Button fullWidth variant="contained" onClick={handleAddPeca} sx={{ height: '100%' }}>
                <AddIcon />
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* Mão de obra */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Mão de Obra (R$)"
            type="number"
            value={formData.mao_obra}
            onChange={(e) => setFormData({ ...formData, mao_obra: parseFloat(e.target.value) || 0 })}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={onCancel} size="small">
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} size="small" sx={{ bgcolor: '#0A2647' }}>
              Adicionar Computador
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}

function OsLoteForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [computadores, setComputadores] = useState([]);
  const [computadoresSelecionados, setComputadoresSelecionados] = useState([]);
  const [showComputadorForm, setShowComputadorForm] = useState(false);

  const [formData, setFormData] = useState({
    cliente_id: '',
    data_abertura: new Date().toISOString().split('T')[0],
    hora_abertura: new Date().toTimeString().slice(0, 5),
    prioridade: 'normal',
    observacoes: '',
    forma_pagamento: 'dinheiro',
    parcelas: 1,
    desconto: 0
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [clientesRes, computadoresRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/computadores')
      ]);
      setClientes(clientesRes.data || []);
      setComputadores(computadoresRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleClienteChange = (clienteId) => {
    setFormData({ ...formData, cliente_id: clienteId });
    setComputadoresSelecionados([]);
  };

  const handleAddComputador = (comp) => {
    setComputadoresSelecionados([...computadoresSelecionados, comp]);
    setShowComputadorForm(false);
    toast.success(`Computador ${comp.computador_nome} adicionado!`);
  };

  const handleRemoveComputador = (index) => {
    const removed = computadoresSelecionados[index];
    setComputadoresSelecionados(computadoresSelecionados.filter((_, i) => i !== index));
    toast.info(`Computador ${removed.computador_nome} removido`);
  };

  const calcularTotalGeral = () => {
    let total = 0;
    computadoresSelecionados.forEach(comp => {
      const pecasTotal = comp.pecas.reduce((sum, p) => sum + (p.valor_total || 0), 0);
      total += (comp.mao_obra || 0) + pecasTotal;
    });
    return total - (formData.desconto || 0);
  };

  const handleSubmit = async () => {
    if (!formData.cliente_id) {
      toast.warning('Selecione um cliente');
      return;
    }

    if (computadoresSelecionados.length === 0) {
      toast.warning('Adicione pelo menos um computador');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        computadores: computadoresSelecionados.map(comp => ({
          computador_id: comp.computador_id,
          servicos: comp.servicos || [],
          pecas: comp.pecas || [],
          diagnosticos: comp.diagnosticos || [],
          relato_cliente: comp.relato_cliente || '',
          mao_obra: comp.mao_obra || 0,
          valor_pecas: comp.pecas.reduce((sum, p) => sum + (p.valor_total || 0), 0)
        }))
      };

      const response = await api.post('/os-lote', payload);
      toast.success(`OS em lote criada com sucesso! ${computadoresSelecionados.length} computadores`);
      navigate(`/os/${response.data.id}`);
    } catch (error) {
      console.error('Erro ao criar OS em lote:', error);
      toast.error(error.response?.data?.error || 'Erro ao criar OS em lote');
    } finally {
      setSaving(false);
    }
  };

  const computadoresDisponiveis = computadores.filter(
    c => c.cliente_id === parseInt(formData.cliente_id)
  );

  if (loading) {
    return (
      <Layout title="OS em Lote">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="OS em Lote">
      <PageHeader
        title="📦 OS em Lote"
        subtitle="Crie uma única OS para múltiplos computadores"
        icon={<ComputerIcon sx={{ color: '#39FF14' }} />}
      />

      <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        {/* Dados Gerais */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Cliente *</InputLabel>
              <Select
                value={formData.cliente_id}
                label="Cliente *"
                onChange={(e) => handleClienteChange(e.target.value)}
              >
                {clientes.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Data"
              type="date"
              value={formData.data_abertura}
              onChange={(e) => setFormData({ ...formData, data_abertura: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Hora"
              type="time"
              value={formData.hora_abertura}
              onChange={(e) => setFormData({ ...formData, hora_abertura: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridade</InputLabel>
              <Select
                value={formData.prioridade}
                label="Prioridade"
                onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
              >
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select
                value={formData.forma_pagamento}
                label="Forma de Pagamento"
                onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
              >
                <MenuItem value="dinheiro">Dinheiro</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="credito">Cartão de Crédito</MenuItem>
                <MenuItem value="debito">Cartão de Débito</MenuItem>
                <MenuItem value="transferencia">Transferência</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Parcelas"
              value={formData.parcelas}
              onChange={(e) => setFormData({ ...formData, parcelas: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Observações Gerais"
              multiline
              rows={2}
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Computadores Selecionados */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
            Computadores ({computadoresSelecionados.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowComputadorForm(true)}
            disabled={!formData.cliente_id || showComputadorForm}
            sx={{ bgcolor: '#0A2647' }}
          >
            Adicionar Computador
          </Button>
        </Box>

        {showComputadorForm && (
          <ComputadorForm
            onAdd={handleAddComputador}
            onCancel={() => setShowComputadorForm(false)}
            clientes={clientes}
            computadoresCliente={computadoresDisponiveis}
          />
        )}

        {/* Lista de Computadores */}
        <AnimatePresence>
          {computadoresSelecionados.map((comp, index) => (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
            >
              <Paper
                sx={{
                  p: 2,
                  mb: 1.5,
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  bgcolor: alpha('#0A2647', 0.02)
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0A2647' }}>
                      <ComputerIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                      {comp.computador_nome}
                    </Typography>
                    {comp.servicos.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                        {comp.servicos.map((s, i) => (
                          <Chip key={i} label={s} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    )}
                    {comp.pecas.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {comp.pecas.map((p, i) => (
                          <Chip
                            key={i}
                            label={`${p.descricao} (${p.quantidade}x)`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    {comp.diagnosticos.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {comp.diagnosticos.map((d, i) => (
                          <Chip key={i} label={d} size="small" color="warning" variant="outlined" />
                        ))}
                      </Box>
                    )}
                    {comp.relato_cliente && (
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        Relato: {comp.relato_cliente}
                      </Typography>
                    )}
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2647' }}>
                      R$ {(
                        (comp.mao_obra || 0) +
                        comp.pecas.reduce((sum, p) => sum + (p.valor_total || 0), 0)
                      ).toFixed(2)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveComputador(index)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          ))}
        </AnimatePresence>

        {computadoresSelecionados.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Nenhum computador adicionado. Clique em "Adicionar Computador" para começar.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Resumo e Total */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total de Computadores: <strong>{computadoresSelecionados.length}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Desconto: R$ {(formData.desconto || 0).toFixed(2)}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">TOTAL GERAL</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0A2647' }}>
              R$ {calcularTotalGeral().toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Botões de Ação */}
        <Box display="flex" gap={1} mt={3} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/os')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving || computadoresSelecionados.length === 0}
            sx={{
              ml: 'auto',
              bgcolor: '#0A2647',
              '&:hover': { bgcolor: '#1a3b5c' }
            }}
          >
            {saving ? 'Salvando...' : 'Criar OS em Lote'}
          </Button>
        </Box>
      </Card>
    </Layout>
  );
}

export default OsLoteForm;
